import { useEffect, useRef } from "react";
import { api } from "../shared/api.js";
import { refreshSession } from "../shared/query.js";

const ACTIVITY = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

/**
 * Eingaben in einem fremden Iframe (SmED) erreichen dieses Fenster nicht. Solange ein solcher
 * Bereich offen ist, gilt das Gerät als bedient – höchstens `HOLD_MAX_MS`, dann greift die Sperre.
 */
const HOLD_MAX_MS = 20 * 60_000;
let holdSince: number | null = null;
export function useIdleHold(active: boolean) {
  useEffect(() => {
    if (!active) return;
    holdSince = Date.now();
    return () => {
      holdSince = null;
    };
  }, [active]);
}

/**
 * Auto-Sperre am Kiosk (ARCHITEKTUR §10.5): nach `seconds` ohne Bedienung → `onIdle`.
 * Bei Aktivität hält ein Keepalive (max. 1×/min) die PIN-Sitzung serverseitig am Leben.
 */
export function useIdle(seconds: number, onIdle: () => void, enabled = true) {
  const cb = useRef(onIdle);
  useEffect(() => {
    cb.current = onIdle;
  });
  useEffect(() => {
    if (!enabled) return;
    let last = Date.now();
    let lastPing = Date.now();
    const on = () => {
      last = Date.now();
      if (last - lastPing > 60_000) {
        lastPing = last;
        void api("/auth/keepalive", { method: "POST" }).catch(() => refreshSession());
      }
    };
    ACTIVITY.forEach((e) => window.addEventListener(e, on, { passive: true }));
    const id = window.setInterval(() => {
      if (holdSince !== null && Date.now() - holdSince < HOLD_MAX_MS) on();
      if (Date.now() - last > seconds * 1000) cb.current();
    }, 1000);
    return () => {
      ACTIVITY.forEach((e) => window.removeEventListener(e, on));
      window.clearInterval(id);
    };
  }, [seconds, enabled]);
}

/** Bildschirm bleibt an, solange ein Vorgang läuft (Screen Wake Lock) */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let stopped = false;
    const acquire = async () => {
      try {
        lock = await navigator.wakeLock.request("screen");
      } catch {
        /* nicht erlaubt – ohne weiter */
      }
    };
    const onVis = () => {
      if (document.visibilityState === "visible" && !stopped) void acquire();
    };
    void acquire();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", onVis);
      void lock?.release().catch(() => undefined);
    };
  }, [active]);
}

/** Globales 401 (Sitzung abgelaufen/gesperrt) → Sitzung neu laden */
export function useUnauthenticatedRefresh() {
  useEffect(() => {
    const on = () => void refreshSession();
    window.addEventListener("omony:unauthenticated", on);
    return () => window.removeEventListener("omony:unauthenticated", on);
  }, []);
}

/** Bildschirmtastatur: fokussiertes Feld in den sichtbaren Bereich scrollen (visualViewport) */
export function useKeepFocusVisible() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const on = () => {
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)
        el.scrollIntoView({ block: "center", behavior: "smooth" });
    };
    vv.addEventListener("resize", on);
    return () => vv.removeEventListener("resize", on);
  }, []);
}
